"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AuthProvider = exports.useAuthContext = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const authorize_1 = require("../api/aut/authorize");
const environment_1 = require("../utils/environment");
const RSI_WORKPLACE_URL = (0, environment_1.byEnvironment)({
    local: 'https://deve.accounts.ricoh.com/portal/top.html',
    development: 'https://deve.accounts.ricoh.com/portal/top.html',
    test: 'https://devu.accounts.ricoh.com/portal/top.html',
    staging: 'https://staging.accounts.ricoh.com/portal/top.html',
    production: 'https://na.accounts.ricoh.com/portal/top.html',
});
const AuthContext = (0, react_1.createContext)({});
/**
 * 認証情報のContextを参照する
 * @returns 認証情報を参照・変更するContext
 */
const useAuthContext = () => (0, react_1.useContext)(AuthContext);
exports.useAuthContext = useAuthContext;
/**
 * 一連の認証処理を行うためのコンテキストプロバイダー
 *
 * ```typescript
 * <AuthProvider
 *      storageType={'local'}
 *      verifierKey={'verifier'}
 *      clientId={''}
 *      redirectURI={''}
 *      accessKey={''}
 *      refreshKey={''}>
 *   <div />
 * </AuthProvider>
 * ```
 * @param AuthProps 認証処理に必要な情報
 */
const AuthProvider = ({ children, storageType, verifierKey, clientId, redirectURI, tokenKey, refreshKey, expireIn, appendScope, }) => {
    const [accessToken, setAccessToken] = (0, react_1.useState)(null);
    const [refreshToken, setRefreshToken] = (0, react_1.useState)(null);
    const value = { accessToken, setAccessToken, refreshToken, setRefreshToken };
    const setVerifier = () => {
        const verifier = (0, authorize_1.createCodeVerifier)();
        if (storageType === 'local') {
            window.localStorage.setItem(verifierKey, verifier);
        }
        else {
            window.sessionStorage.setItem(verifierKey, verifier);
        }
        return verifier;
    };
    const getVerifier = () => storageType === 'local' ? window.localStorage.getItem(verifierKey) : window.sessionStorage.getItem(verifierKey);
    const setAccessTokenToStorage = (token) => {
        if (token) {
            if (storageType === 'local') {
                window.localStorage.setItem(tokenKey, token);
            }
            else {
                window.sessionStorage.setItem(tokenKey, token);
            }
        }
    };
    const setRefreshTokenToStorage = (token) => {
        if (refreshKey && token) {
            if (storageType === 'local') {
                window.localStorage.setItem(refreshKey, token);
            }
            else {
                window.sessionStorage.setItem(refreshKey, token);
            }
        }
    };
    const getStorageAccessToken = () => {
        return storageType === 'local' ? window.localStorage.getItem(tokenKey) : window.sessionStorage.getItem(tokenKey);
    };
    const getStorageRefreshToken = () => {
        if (!refreshKey) {
            return null;
        }
        return storageType === 'local'
            ? window.localStorage.getItem(refreshKey)
            : window.sessionStorage.getItem(refreshKey);
    };
    const clearStorage = () => {
        if (storageType === 'local') {
            window.localStorage.removeItem(verifierKey);
            window.localStorage.removeItem(tokenKey);
            if (refreshKey)
                window.localStorage.removeItem(refreshKey);
        }
        else {
            window.sessionStorage.removeItem(verifierKey);
            window.sessionStorage.removeItem(tokenKey);
            if (refreshKey)
                window.sessionStorage.removeItem(refreshKey);
        }
    };
    const initAuthorize = () => __awaiter(void 0, void 0, void 0, function* () {
        // URLから認可コード取得
        const code = (0, authorize_1.getParam)('code');
        if (code) {
            const verifier = getVerifier() || setVerifier();
            const response = yield (0, authorize_1.getToken)(redirectURI, code, verifier, clientId, expireIn);
            if (response) {
                setAccessTokenToStorage(response.access_token);
                setRefreshTokenToStorage(response.refresh_token);
                window.location.assign(redirectURI);
                return;
            }
            else {
                window.location.assign(RSI_WORKPLACE_URL);
                return;
            }
        }
        // URLから認可コード取得エラー検知
        if ((0, authorize_1.getParam)('error')) {
            window.location.assign(RSI_WORKPLACE_URL);
            return;
        }
        // 認可コード取得要求
        if (getStorageAccessToken() === null) {
            const verifier = setVerifier();
            const challenge = (0, authorize_1.createCodeChallenge)(verifier);
            const authURL = appendScope === undefined
                ? (0, authorize_1.createAuthorizeURL)(clientId, challenge, redirectURI)
                : (0, authorize_1.createAuthorizeURL)(clientId, challenge, redirectURI, ...appendScope);
            window.location.assign(authURL);
            return;
        }
        // アクセストークン検証
        const accessToken = getStorageAccessToken();
        const refreshToken = getStorageRefreshToken();
        const result = yield (0, authorize_1.introspectToken)(accessToken, clientId);
        if (!result) {
            // トークン不正 or 期限切れのためWeb Storageを削除してリロードする
            clearStorage();
            window.location.assign(redirectURI);
            return;
        }
        setAccessToken(accessToken);
        setRefreshToken(refreshToken);
    });
    (0, react_1.useEffect)(() => {
        initAuthorize();
    }, []);
    return (0, jsx_runtime_1.jsx)(AuthContext.Provider, Object.assign({ value: value }, { children: children }));
};
exports.AuthProvider = AuthProvider;
